/*
 * @Author: Ryan
 * @Date: 2026-02-04
 * @Description: AI TikTok Video Generator SEO page - /features/ai-tiktok-video-generator
 */
import type { GetStaticProps } from 'next';
import React from 'react';

import { withLandingLayout } from '@/landing/components/LandingLayout';
import SEOHead, { type SEOHeadProps } from '@/landing/components/SEOHead';
import SEOSections from '@/landing/tiktok-video-generator/seo';
import {
  createBreadcrumbSchema,
  createFAQSchema,
  createOrganizationSchema,
  createSoftwareAppSchema,
} from '@/landing/utils/structuredData';

const PAGE_URL = 'https://falcocut.ai/features/ai-tiktok-video-generator';

const FAQ_SCHEMA_DATA = [
  {
    question: 'What is FalcoCut?',
    answer:
      'FalcoCut is an AI-powered video creation platform designed for short-form content. It helps you turn scripts, product links, images, or existing clips into ready-to-publish videos for platforms like TikTok — without manual editing.',
  },
  {
    question: 'Can I make TikTok videos without filming myself?',
    answer:
      'Yes. You can create videos using AI avatars, voiceovers, generated visuals, or existing media. No camera, studio, or on-screen appearance is required.',
  },
  {
    question: 'How do I create a video with FalcoCut?',
    answer:
      'Start by entering a script, pasting a product or webpage link, or uploading your own assets. FalcoCut generates a video draft that you can customize before exporting.',
  },
  {
    question: 'Do I need editing experience?',
    answer:
      "No. FalcoCut is built for non-editors. You don't need prior video editing skills to create polished, platform-ready videos.",
  },
  {
    question: 'Is there a free plan?',
    answer:
      'FalcoCut offers a free plan so you can try the platform and explore its core features. Paid plans unlock higher limits and advanced capabilities.',
  },
];

const SEO_CONFIG: SEOHeadProps = {
  title: 'AI TikTok Video Generator | Create Viral TikTok Videos with FalcoCut',
  description:
    'Turn scripts, links, or clips into viral TikTok videos in minutes. FalcoCut uses AI to handle editing, captions, voice, and music — no complex editing required.',
  url: PAGE_URL,
  image: 'https://falcocut.ai/images/og-img.png',
  imageAlt: 'FalcoCut AI TikTok Video Generator',
  publishedTime: '2026-01-13',
  modifiedTime: '2026-02-04',
  preloadImages: [
    'https://assets.falcocut.ai/tools/tiktok-video-generator/hero-work-bag.webp',
  ],
  structuredData: [
    createSoftwareAppSchema({
      name: 'FalcoCut AI TikTok Video Generator',
      description:
        'Turn scripts, links, or clips into viral TikTok videos in minutes. FalcoCut uses AI to handle editing, captions, voice, and music — no complex editing required.',
      url: PAGE_URL,
      applicationCategory: 'MultimediaApplication',
      ratingValue: '4.8',
      ratingCount: '1250',
      featureList: [
        'AI TikTok Video Generation',
        'Text to Video',
        'Trend Style Clips',
        'Auto Voiceovers & Captions',
        'Long to Shorts Conversion',
      ],
    }),
    createOrganizationSchema({
      name: 'FalcoCut',
      url: 'https://falcocut.ai',
      logo: 'https://falcocut.ai/images/logo.png',
    }),
    createFAQSchema({
      faqs: FAQ_SCHEMA_DATA,
    }),
    createBreadcrumbSchema({
      items: [
        { name: 'Home', url: 'https://falcocut.ai' },
        { name: 'AI TikTok Video Generator', url: PAGE_URL },
      ],
    }),
  ],
};

const TikTokVideoGeneratorPage: React.FC = () => {
  return (
    <>
      <SEOHead {...SEO_CONFIG} />
      <SEOSections />
    </>
  );
};

export default withLandingLayout(TikTokVideoGeneratorPage);

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {},
    revalidate: 3600,
  };
};
